"use client";
import { BMButton, BMButtonIcon, BMPrimitives } from "@traveloka/district-bloom";
import IcSystemChevronLeft from "@traveloka/icon-kit-web/react/IcSystemChevronLeft";
import IcSystemChevronRight from "@traveloka/icon-kit-web/react/IcSystemChevronRight";
import { RefObject } from "react";
import { FlatList, ImageStyle, StyleSheet, View } from "react-native";
import { appendTestId } from "../../utils";
import BMView from "../View/BMView";
import { ACTIVE_DOT_SIZE } from "./constants";
import SpotlightCardBody from "./SpotlightCardBody";
import SpotlightDot from "./SpotlightDot";
import { SpotlightPaginationItem } from "./types";
import useSpotlightDotsAnimation from "./useSpotlightDotsAnimation";

type Props = {
  items: SpotlightPaginationItem[];
  total: number;
  currentIndex: number;
  isFirst: boolean;
  isLast: boolean;
  cardButtonLabel?: string;
  itemWidth?: number;
  flatListRef: RefObject<FlatList<SpotlightPaginationItem> | null>;
  imageStyle: ImageStyle;
  testID?: string;
  onMeasureWidth: (width: number) => void;
  onForward: () => void;
  onBack: () => void;
};

export default function SpotlightCarousel({
  items,
  total,
  currentIndex,
  isFirst,
  isLast,
  cardButtonLabel,
  itemWidth,
  flatListRef,
  imageStyle,
  testID,
  onMeasureWidth,
  onForward,
  onBack,
}: Props) {
  const progress = useSpotlightDotsAnimation(total, currentIndex);
  const dotsTestID = appendTestId("dots", testID);

  return (
    <BMView flex justify="between">
      <View
        onLayout={(event) => onMeasureWidth(event.nativeEvent.layout.width)}
      >
        {itemWidth !== undefined && (
          <FlatList
            ref={flatListRef}
            data={items}
            horizontal
            scrollEnabled={false}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.key}
            getItemLayout={(_, index) => ({
              length: itemWidth,
              offset: itemWidth * index,
              index,
            })}
            renderItem={({ item }) => (
              <SpotlightCardBody
                style={{ width: itemWidth, flexShrink: 0 }}
                title={item.title}
                description={item.description}
                imageSource={item.imageSource}
                imageStyle={imageStyle}
                testID={appendTestId(testID, item.key)}
              />
            )}
          />
        )}
      </View>
      <BMView row align="center" style={styles.controlsRow}>
        <BMView flex align="start">
          <BMButtonIcon
            Icon={IcSystemChevronLeft}
            size="LARGE"
            disabled={isFirst || itemWidth === undefined}
            onPress={onBack}
            testID={appendTestId("button_prev", testID)}
          />
        </BMView>
        <BMView row align="center" style={styles.dotsRow} testID={dotsTestID}>
          {progress.map((value, index) => (
            <SpotlightDot
              key={index}
              value={value}
              spacing={index > 0}
              testID={appendTestId(dotsTestID, `dot_${index}`)}
            />
          ))}
        </BMView>
        <BMView flex align="end">
          {isLast ? (
            cardButtonLabel && (
              <BMButton
                size="LARGE"
                label={cardButtonLabel}
                onPress={onForward}
                testID={appendTestId("button", testID)}
              />
            )
          ) : (
            <BMButtonIcon
              Icon={IcSystemChevronRight}
              size="LARGE"
              disabled={itemWidth === undefined}
              onPress={onForward}
              testID={appendTestId("button_next", testID)}
            />
          )}
        </BMView>
      </BMView>
    </BMView>
  );
}

const styles = StyleSheet.create({
  controlsRow: { marginTop: BMPrimitives.BMSize.lg },
  dotsRow: { height: ACTIVE_DOT_SIZE },
});
